/**
 * Utility functions for handling onboarding localStorage keys
 */

/**
 * Generates a user-specific localStorage key for onboarding data
 * @param userEmail - The user's email address
 * @returns The localStorage key for the user's onboarding data
 */
export const getOnboardingStorageKey = (userEmail?: string | null): string => {
	if (!userEmail) {
		return 'onboarding_completed_anonymous';
	}

	// Sanitize email to create a valid localStorage key
	// Replace special characters with underscores
	const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '_');
	return `onboarding_completed_${sanitizedEmail}`;
};

/**
 * Gets onboarding data from localStorage for a specific user
 * @param userEmail - The user's email address
 * @returns The parsed onboarding data or null if not found
 */
export const getOnboardingData = (userEmail?: string | null) => {
	const key = getOnboardingStorageKey(userEmail);
	const data = localStorage.getItem(key);

	if (data) {
		try {
			const parsed = JSON.parse(data);

			// Validate the parsed data structure
			if (parsed && typeof parsed === 'object') {
				return parsed;
			} else {
				console.warn('Onboarding data is not an object:', parsed);
				return null;
			}
		} catch (error) {
			console.error('Error parsing onboarding data:', error, 'Raw data:', data);

			// If the data is malformed, remove it to prevent future errors
			try {
				localStorage.removeItem(key);
			} catch (removeError) {
				console.error('Error removing malformed data:', removeError);
			}

			return null;
		}
	}

	return null;
};

/**
 * Saves onboarding data to localStorage for a specific user
 * @param userEmail - The user's email address
 * @param data - The onboarding data to save
 */
export const saveOnboardingData = (
	userEmail: string | null | undefined,
	data: any,
) => {
	const key = getOnboardingStorageKey(userEmail);
	localStorage.setItem(key, JSON.stringify(data));
};

/**
 * Removes onboarding data from localStorage for a specific user
 * @param userEmail - The user's email address
 */
export const removeOnboardingData = (userEmail?: string | null) => {
	const key = getOnboardingStorageKey(userEmail);
	localStorage.removeItem(key);
};

/**
 * Migrates old onboarding data to new user-specific format
 * @param userEmail - The user's email address
 * @param oldKey - The old localStorage key to migrate from
 */
export const migrateOnboardingData = (
	userEmail: string,
	oldKey: string = 'onboarding_completed',
) => {
	try {
		const oldData = localStorage.getItem(oldKey);
		if (oldData) {
			let parsedData;
			try {
				parsedData = JSON.parse(oldData);
			} catch (error) {
				// If it's not valid JSON, try to extract useful information
				if (oldData.includes('completed') || oldData.includes('currentStep')) {
					// Try to create a basic structure
					parsedData = {
						completed: oldData.includes('completed'),
						currentStep: 1,
						stepsCompleted: [],
						skippedSteps: [],
						uploadedFileId: null,
					};
				} else {
					parsedData = null;
				}
			}

			if (parsedData) {
				// Save to new user-specific key
				saveOnboardingData(userEmail, parsedData);
				// Remove old data
				localStorage.removeItem(oldKey);
				return parsedData;
			}
		}
		return null;
	} catch (error) {
		console.error('Error migrating onboarding data:', error);
		return null;
	}
};

/**
 * Cleans up old onboarding data and migrates it to new user-specific format
 * @param userEmail - The user's email address
 */
export const cleanupOldOnboardingData = (userEmail: string) => {
	try {
		if (!userEmail) return;

		// Try to migrate old onboarding data first
		migrateOnboardingData(userEmail, 'onboarding_completed');

		// Also clean up any other old keys that might exist
		const keysToClean = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (
				key &&
				key.startsWith('onboarding_') &&
				key !== getOnboardingStorageKey(userEmail)
			) {
				keysToClean.push(key);
			}
		}

		keysToClean.forEach((key) => {
			localStorage.removeItem(key);
		});
	} catch (error) {
		console.error('Error cleaning up old onboarding data:', error);
	}
};
